//@flow
import { createSelector, type OutputSelector } from 'reselect';
import { personnelSelector } from 'common/redux/selectors/sessionSelector';

import { type AppState } from 'common/redux/reducers';

import RoleOperationEnum from 'common/enums/RoleOperationEnum';
import DashboardSectionEnum, {
    type DashboardSectionEnumType
} from 'common/enums/DashboardSectionEnum';

import { SectionVisibilitySettings } from 'common/models/internal/SectionVisibilitySettings';
import { getPlatformSettings } from 'common/modules/platform-settings';

export const uploadInProgressSelector = (state: Object): boolean => {
    return !!state.session.get('isUploadInProgress');
};

export const uploadedImageSelector = createSelector(
    personnelSelector,
    personnel => {
        if (personnel) {
            return personnel.get('photoUrl');
        }

        return '';
    }
);

export const personnelVersionSelector = createSelector(
    personnelSelector,
    personnel => {
        if (personnel) {
            return personnel.get('version');
        }

        return '';
    }
);

export const getSectionVisibilitySettings: OutputSelector<
    AppState,
    $FixMe,
    SectionVisibilitySettings
> = createSelector([personnelSelector], personnel => {
    const {
        sectionVisibilitySettings: visibilitySettings
    } = getPlatformSettings();

    return SectionVisibilitySettings.fromJS({
        showBatchSection: Boolean(
            visibilitySettings.showBatchSection &&
                personnel?.hasPermission(RoleOperationEnum.SHOW_BATCH_SECTION)
        ),

        showInsightsSection: Boolean(
            visibilitySettings.showInsightsSection &&
                personnel?.hasPermission(
                    RoleOperationEnum.SHOW_INSIGHTS_SECTION
                )
        ),

        showLiveViewSection: Boolean(
            visibilitySettings.showLiveViewSection &&
                personnel?.hasPermission(
                    RoleOperationEnum.SHOW_LIVE_VIEW_SECTION
                )
        ),

        showPjpSection: Boolean(
            visibilitySettings.showPjpSection &&
                personnel?.hasPermission(RoleOperationEnum.SHOW_PJP_SECTION)
        ),

        showEntitySection: Boolean(
            visibilitySettings.showEntitySection &&
                personnel?.hasPermission(RoleOperationEnum.SHOW_ENTITY_SECTION)
        ),

        showAutosortLiveViewSection: Boolean(
            visibilitySettings.showAutosortLiveViewSection &&
                personnel?.hasPermission(
                    RoleOperationEnum.SHOW_AUTOSORT_LIVE_VIEW_SECTION
                )
        ),

        showOrderManagementSection: Boolean(
            visibilitySettings.showOrderManagementSection &&
                personnel?.hasPermission(
                    RoleOperationEnum.SHOW_ORDER_MANAGEMENT_SECTION
                )
        ),

        showShipmentManagementSection: Boolean(
            visibilitySettings.showShipmentManagementSection &&
                personnel?.hasPermission(
                    RoleOperationEnum.SHOW_SHIPMENT_MANAGEMENT_SECTION
                )
        ),

        showTourDispatchManagementSection: Boolean(
            visibilitySettings.showTourDispatchManagementSection &&
                personnel?.hasPermission(
                    RoleOperationEnum.SHOW_TOUR_DISPATCH_MANAGEMENT_SECTION
                )
        ),

        showQuoteManagementSection: Boolean(
            visibilitySettings.showQuoteManagementSection &&
                personnel?.hasPermission(
                    RoleOperationEnum.SHOW_QUOTE_MANAGEMENT_SECTION
                )
        ),

        showNetOptSection: Boolean(
            visibilitySettings.showNetOptSection &&
                personnel?.hasPermission(RoleOperationEnum.SHOW_NETOPT_SECTION)
        ),

        showRosterSection: Boolean(
            visibilitySettings.showRosterSection &&
                personnel?.hasPermission(RoleOperationEnum.SHOW_ROSTER_SECTION)
        ),

        showContractSection:
            visibilitySettings.showContractSection &&
            personnel?.hasPermission(RoleOperationEnum.SHOW_CONTRACTS_SECTION),

        showOrchestrationSection: visibilitySettings.showOrchestrationSection
    });
});

export const getDefaultVisibleSection: OutputSelector<
    AppState,
    $FixMe,
    DashboardSectionEnumType
> = createSelector([getSectionVisibilitySettings], visibilitySettings => {
    switch (true) {
        case visibilitySettings.showLiveViewSection:
            return DashboardSectionEnum.LIVE_VIEW;

        case visibilitySettings.showBatchSection:
            return DashboardSectionEnum.BATCH;

        case visibilitySettings.showPjpSection:
            return DashboardSectionEnum.PJP;

        case visibilitySettings.showInsightsSection:
            return DashboardSectionEnum.INSIGHTS;

        case visibilitySettings.showNetOptSection:
            return DashboardSectionEnum.NETOPT_ENTITIES;

        case visibilitySettings.showEntitySection:
            return DashboardSectionEnum.ENTITIES;

        case visibilitySettings.showAutosortLiveViewSection:
            return DashboardSectionEnum.INTELLISORT;

        case visibilitySettings.showQuoteManagementSection:
            return DashboardSectionEnum.QUOTATIONS;

        case visibilitySettings.showContractSection:
            return DashboardSectionEnum.CONTRACTS;
        case visibilitySettings.showRosterSection:
            return DashboardSectionEnum.ROSTER;

        default:
            return DashboardSectionEnum.MY_SETTINGS;
    }
});
